import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { DataGrid, GridColDef, GridToolbarQuickFilter } from '@mui/x-data-grid';
import { esES } from '@mui/x-data-grid/locales';
import AddIcon from '@mui/icons-material/Add';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';

import dayjs from 'dayjs';

import useAxios from '../utils/useAxios';
import PageShell from './layout/PageShell';
import { SectionCard, EmptyState, currency } from './ui';
import { TRUE_COLORS } from '../theme';
import { Category } from '../types';
import { moduleOn } from '../utils/features';
import { ShellContext } from './layout/AppShell';

const Products = () => {
  const axios = useAxios();
  const navigate = useNavigate();
  const { features } = React.useContext(ShellContext);

  const [tab, setTab] = React.useState<'active' | 'inactive'>('active');
  const [active, setActive] = React.useState<any[]>([]);
  const [inactive, setInactive] = React.useState<any[]>([]);
  const [categories, setCategories] = React.useState<Category[]>([]);
  const [category, setCategory] = React.useState('all');
  const [loading, setLoading] = React.useState(true);
  // Sold by cashiers without being in the catalog (Por registrar).
  const [pendingCount, setPendingCount] = React.useState(0);
  React.useEffect(() => {
    axios.get('api/pending-products/').then(r => setPendingCount((r.data ?? []).length)).catch(() => setPendingCount(0));
  }, [axios]);

  React.useEffect(() => {
    (async () => {
      const [a, i, c] = await Promise.allSettled([
        axios.get('api/products/'),
        axios.get('api/deleted-products/'),
        axios.get('api/categories/'),
      ]);
      if (a.status === 'fulfilled') setActive(a.value.data ?? []);
      if (i.status === 'fulfilled') setInactive(i.value.data ?? []);
      if (c.status === 'fulfilled') setCategories(c.value.data ?? []);
      setLoading(false);
    })();
  }, [axios]);

  const rows = (tab === 'active' ? active : inactive).filter(p =>
    category === 'all' || (p.category ?? []).some((c: Category) => String(c.id) === category),
  );

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Producto',
      flex: 1.6,
      minWidth: 240,
      renderCell: ({ row }) => (
        <Stack sx={{ justifyContent: 'center', height: '100%', minWidth: 0 }}>
          <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
            <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }} noWrap>{row.name}</Typography>
            {!row.display && row.enabled && (
              <VisibilityOffOutlinedIcon titleAccess="Oculto en POS" sx={{ fontSize: 16, color: TRUE_COLORS.secondary }} />
            )}
          </Stack>
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }} noWrap>
            {row.barcode}{row.sku ? ` · SKU ${row.sku}` : ''}
          </Typography>
        </Stack>
      ),
    },
    {
      field: 'category',
      headerName: 'Categorías',
      flex: 1,
      minWidth: 160,
      sortable: false,
      valueGetter: (value: Category[]) => (value ?? []).map(c => c.name).join(', '),
      renderCell: ({ row }) => (
        <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', height: '100%', overflow: 'hidden' }}>
          {(row.category ?? []).slice(0, 2).map((c: Category) => <Chip key={c.id} size="small" label={c.name} />)}
          {(row.category ?? []).length > 2 && <Typography variant="body2" sx={{ color: 'text.secondary' }}>+{row.category.length - 2}</Typography>}
        </Stack>
      ),
    },
    { field: 'price', headerName: 'Precio', type: 'number', flex: 0.6, minWidth: 110, valueFormatter: v => currency(v) },
    { field: 'cost', headerName: 'Costo', type: 'number', flex: 0.6, minWidth: 110, valueFormatter: v => currency(v) },
    {
      field: 'margin',
      headerName: 'Margen',
      type: 'number',
      flex: 0.5,
      minWidth: 100,
      valueGetter: (_v, row: any) => (row.price > 0 ? (row.price - row.cost) / row.price : null),
      renderCell: ({ value }) => value == null ? '—' : (
        <Box component="span" sx={{ fontWeight: 600, color: value < 0 ? TRUE_COLORS.danger : value < 0.15 ? TRUE_COLORS.warning : TRUE_COLORS.success }}>
          {(value * 100).toFixed(0)}%
        </Box>
      ),
    },
    {
      field: 'last_modified',
      headerName: 'Modificado',
      flex: 0.6,
      minWidth: 120,
      type: 'dateTime',
      valueGetter: v => (v ? new Date(v) : null),
      valueFormatter: (v: Date | null) => (v ? dayjs(v).fromNow() : '—'),
    },
  ];

  const empty = !loading && active.length === 0 && inactive.length === 0;

  return (
    <PageShell
      title="Productos"
      subtitle="Tu catálogo: precios, costos y visibilidad en TRUE POS"
      actions={
        <>
          <Button variant="outlined" startIcon={<CategoryOutlinedIcon />} onClick={() => navigate('/admin/categories')}>Categorías</Button>
          {moduleOn(features, 'options') && <Button variant="outlined" startIcon={<TuneOutlinedIcon />} onClick={() => navigate('/admin/modifiers')}>Opciones</Button>}
          <Button variant="outlined" startIcon={<StorefrontOutlinedIcon />} onClick={() => navigate('/admin/common-products')}>Productos comunes</Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/admin/add-product')}>Agregar producto</Button>
        </>
      }
    >
      {pendingCount > 0 && (
        <Alert severity="info" sx={{ mb: 2 }} data-testid="pending-banner"
          action={<Button color="inherit" size="small" onClick={() => navigate('/admin/products/pending')}>Revisar</Button>}>
          {pendingCount === 1 ? 'Un producto se vendió' : `${pendingCount} productos se vendieron`} sin estar en tu catálogo.
        </Alert>
      )}
      {empty ? (
        <SectionCard>
          <EmptyState
            icon={<Inventory2OutlinedIcon />}
            title="Agrega tu primer producto"
            description="Elige productos comunes (refrescos, botanas, lácteos…) y ponles precio, o registra los tuyos con código de barras, precio y stock. Aparecen en TRUE POS."
            action={(
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <Button variant="contained" startIcon={<StorefrontOutlinedIcon />} onClick={() => navigate('/admin/common-products')}>Empezar con productos comunes</Button>
                <Button variant="outlined" startIcon={<AddIcon />} onClick={() => navigate('/admin/add-product')}>Agregar producto</Button>
              </Stack>
            )}
          />
        </SectionCard>
      ) : (
        <SectionCard noPadding>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            sx={{ px: 2, pt: 1, justifyContent: 'space-between', alignItems: { md: 'center' }, borderBottom: `1px solid ${TRUE_COLORS.borderSolid}` }}
          >
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 'none' }}>
              <Tab value="active" label={`Activos (${active.length})`} />
              <Tab value="inactive" label={`Inactivos (${inactive.length})`} />
            </Tabs>
            <TextField select size="small" label="Categoría" value={category} onChange={e => setCategory(e.target.value)} sx={{ minWidth: 200, my: 1 }}>
              <MenuItem value="all">Todas</MenuItem>
              {categories.map(c => <MenuItem key={c.id} value={String(c.id)}>{c.name}</MenuItem>)}
            </TextField>
          </Stack>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            autoHeight
            rowHeight={60}
            pageSizeOptions={[25, 50, 100]}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } }, sorting: { sortModel: [{ field: 'name', sort: 'asc' }] } }}
            onRowClick={({ row }) => navigate(`/admin/products/${row.id}`)}
            slots={{
              toolbar: () => (
                <Box sx={{ px: 2, pt: 2, pb: 1 }}>
                  <GridToolbarQuickFilter placeholder="Buscar por nombre, código o SKU…" debounceMs={200} sx={{ width: { xs: '100%', sm: 360 } }} />
                </Box>
              ),
              noRowsOverlay: () => (
                <EmptyState compact title={tab === 'active' ? 'Sin productos en esta categoría' : 'No hay productos inactivos'} />
              ),
            }}
            localeText={esES.components.MuiDataGrid.defaultProps.localeText}
            sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' }, '--DataGrid-overlayHeight': '200px' }}
          />
        </SectionCard>
      )}
    </PageShell>
  );
};

export default Products;
